/**
 * Password rules, mirroring core/password_policy.py on the backend.
 *
 * The codes are the same on both sides, so one set of translations covers a
 * check made here and a rejection that came back from the API.
 */

export const PASSWORD_MIN_LENGTH = 8;

export const PASSWORD_ERRORS = {
    tooShort: "password_too_short",
    noUppercase: "password_no_uppercase",
    noLowercase: "password_no_lowercase",
    noDigit: "password_no_digit",
    noSpecial: "password_no_special",
    notMatch: "passwords_not_match",
    sameAsCurrent: "password_same_as_current",
} as const;

/** Codes of every rule the password breaks; empty means it is acceptable. */
export const validatePassword = (password: string): string[] => {
    const value = password || "";
    const errors: string[] = [];

    if (value.length < PASSWORD_MIN_LENGTH) errors.push(PASSWORD_ERRORS.tooShort);
    if (!/[A-ZА-ЯЄІЇҐ]/.test(value)) errors.push(PASSWORD_ERRORS.noUppercase);
    if (!/[a-zа-яєіїґ]/.test(value)) errors.push(PASSWORD_ERRORS.noLowercase);
    if (!/[0-9]/.test(value)) errors.push(PASSWORD_ERRORS.noDigit);
    // Anything that is neither a letter, a digit nor whitespace counts, which
    // is the same permissive rule the backend applies.
    if (!/[^\p{L}\p{N}\s]/u.test(value)) errors.push(PASSWORD_ERRORS.noSpecial);

    return errors;
};

/**
 * First problem with a candidate password, as a translation key.
 * `repeat` is compared too, so a caller has one place to ask.
 */
export const passwordProblem = (password: string, repeat?: string): string | null => {
    const [firstBrokenRule] = validatePassword(password);
    if (firstBrokenRule) return `password_rules.${firstBrokenRule}`;
    if (repeat !== undefined && password !== repeat) {
        return `password_rules.${PASSWORD_ERRORS.notMatch}`;
    }
    return null;
};

/** Translation key for a code the API answered with. */
export const passwordErrorKey = (code?: string): string | null =>
    code && Object.values(PASSWORD_ERRORS).includes(code as any)
        ? `password_rules.${code}`
        : null;
